import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle,
  Paper, Stack, TextField, Typography, MenuItem,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { isApiErrorStatus } from '../../services/api/http';
import { PaymentPlan } from '../../types/paymentPlan';
import { PaymentMethod } from '../../types';
import { formatCurrency } from '../../utils/currency';
import { rateLimitErrorMessage } from '../../utils/errorMessage';
import { createCustomerDebtPdf } from '../../utils/customerDebtPdf';
import { buildWhatsAppUrl } from '../../utils/whatsapp';

type Props = { customerId: string; customerName: string; customerPhone?: string; canManage: boolean; onChanged: () => void; refreshKey?: number };

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function schedulePreview(total: string, count: string, start: string): { date: string; cents: number }[] {
  if (!/^\d+(\.\d{1,2})?$/.test(total) || !/^[1-9]\d*$/.test(count) || !/^\d{4}-\d{2}-\d{2}$/.test(start)) return [];
  const [year, month, day] = start.split('-').map(Number);
  const parts = total.split('.');
  const cents = Number(parts[0]) * 100 + Number((parts[1] || '').padEnd(2, '0'));
  const n = Number(count);
  const first = new Date(Date.UTC(year, month - 1, day));
  if (!Number.isSafeInteger(cents) || n < 1 || n > 120 || Math.floor(cents / n) < 1 ||
      first.getUTCFullYear() !== year || first.getUTCMonth() !== month - 1 || first.getUTCDate() !== day) return [];
  const each = Math.floor(cents / n);
  return Array.from({ length: n }, (_, i) => {
    const index = month - 1 + i;
    const y = year + Math.floor(index / 12);
    const m = index % 12;
    const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return { date: `${y}-${String(m + 1).padStart(2, '0')}-${String(Math.min(day, lastDay)).padStart(2, '0')}`,
      cents: i === n - 1 ? cents - each * (n - 1) : each };
  });
}

export default function CustomerPaymentPlans(props: Props) {
  if (!apiClient.paymentPlans) return null;
  return <Plans {...props} />;
}

function Plans({ customerId, customerName, customerPhone, canManage, onChanged, refreshKey }: Props) {
  const { language, t } = useLanguage();
  const tr = language === 'tr';
  const label = (turkish: string, english: string) => tr ? turkish : english;
  const [plans, setPlans] = useState<PaymentPlan[]>([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentPlan | null>(null);
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [cancelPlan, setCancelPlan] = useState<PaymentPlan | null>(null);
  const [reversal, setReversal] = useState<{ planId: string; paymentId: string } | null>(null);
  const [amount, setAmount] = useState('');
  const [title, setTitle] = useState('');
  const [count, setCount] = useState('6');
  const [firstDueDate, setFirstDueDate] = useState(today());
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const preview = schedulePreview(amount, count, firstDueDate);
  let previewRemainingCents = Math.round(Number(paymentAmount) * 100);
  const paymentPreview = paymentPlan && Number.isSafeInteger(previewRemainingCents) && previewRemainingCents > 0
    ? paymentPlan.installments.map(item => {
      const appliedCents = Math.min(previewRemainingCents, Math.round(item.remainingAmount * 100));
      previewRemainingCents -= appliedCents;
      return { sequence: item.sequenceNo, cents: appliedCents };
    }).filter(item => item.cents > 0) : [];

  const load = useCallback(async () => {
    try {
      const first = await apiClient.paymentPlans.list(customerId);
      const rest = await Promise.all(Array.from({ length: Math.max(0, first.totalPages - 1) }, (_, i) =>
        apiClient.paymentPlans.list(customerId, i + 1)));
      setPlans([first, ...rest].flatMap(page => page.content));
      setAvailable(true);
    } catch (e) {
      if (isApiErrorStatus(e, 404)) setAvailable(false);
      else setError(rateLimitErrorMessage(e, tr ? 'Ödeme planları yüklenemedi.' : 'Could not load payment plans.', t));
    } finally { setLoading(false); }
  }, [customerId, tr, t]);
  useEffect(() => { void load(); }, [load, refreshKey]);

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await work(); setCreateOpen(false); setEditing(null); setPaymentPlan(null); setCancelPlan(null); setReversal(null); await load(); onChanged(); }
    catch (e) { setError(rateLimitErrorMessage(e, tr ? 'İşlem tamamlanamadı.' : 'The action failed.', t)); }
    finally { setBusy(false); }
  };

  const planMessage = (plan: PaymentPlan) => [
    label('Ödeme planı', 'Payment plan'), customerName, plan.title || '',
    `${label('Durum', 'Status')}: ${statusLabel(plan.status)}`,
    `${label('Toplam', 'Total')}: ${formatCurrency(plan.totalAmount)}`,
    `${label('Ödenen', 'Paid')}: ${formatCurrency(plan.paidAmount)}`,
    `${label('Kalan', 'Remaining')}: ${formatCurrency(plan.remainingAmount)}`,
    '', ...plan.installments.map(item => `${item.sequenceNo}. ${item.dueDate} — ${formatCurrency(item.amount)} · ${statusLabel(item.status)}`),
  ].filter(Boolean).join('\n');

  const reminderMessage = (plan: PaymentPlan) => {
    const next = plan.installments.find(item => item.remainingAmount > 0);
    return next ? `${label('Ödeme hatırlatması', 'Payment reminder')}\n${customerName}\n${plan.title || ''}\n${label('Vade', 'Due date')}: ${next.dueDate}\n${label('Kalan taksit', 'Remaining installment')}: ${formatCurrency(next.remainingAmount)}` : planMessage(plan);
  };

  const downloadPlan = async (plan: PaymentPlan) => {
    setBusy(true); setError('');
    try {
      const blob = await createCustomerDebtPdf({ pageSize: 'A4', pageMargins: [40, 40, 40, 40],
        defaultStyle: { font: 'Roboto', fontSize: 11 }, content: [{ text: planMessage(plan) }] });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = 'odeme-plani.pdf'; link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(rateLimitErrorMessage(e, label('PDF oluşturulamadı.', 'Could not create PDF.'), t)); }
    finally { setBusy(false); }
  };

  if (!available) return null;
  const statusLabel = (status: string) => ({
    ACTIVE: label('Aktif', 'Active'), COMPLETED: label('Tamamlandı', 'Completed'),
    CANCELLED: label('İptal edildi', 'Cancelled'), PENDING: label('Bekliyor', 'Pending'),
    OVERDUE: label('Gecikmiş', 'Overdue'), PARTIALLY_PAID: label('Kısmi ödendi', 'Partially paid'),
    PAID: label('Ödendi', 'Paid'),
  }[status] || status);

  return <Box sx={{ my: 2 }}>
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="h6">{label('Ödeme planları', 'Payment plans')}</Typography>
        {canManage && <Button variant="outlined" onClick={() => {
          setError(''); setEditing(null); setTitle(''); setAmount(''); setCount('6'); setFirstDueDate(today()); setCreateOpen(true);
        }}>{label('Taksitli borç ekle', 'Add installment plan')}</Button>}
      </Stack>
      {loading && <CircularProgress size={20} sx={{ mt: 2 }} />}
      {error && !createOpen && !paymentPlan && !cancelPlan && !reversal && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {!loading && plans.length === 0 && <Typography color="text.secondary" sx={{ mt: 1 }}>{label('Henüz ödeme planı yok.', 'No payment plans yet.')}</Typography>}
      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
        {plans.map(plan => <Paper variant="outlined" key={plan.id} data-testid={`payment-plan-card-${plan.id}`} sx={{ p: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography fontWeight={700}>{plan.title || label('Taksitli borç', 'Installment debt')}</Typography>
            <Chip size="small" label={statusLabel(plan.status)} />
          </Stack>
          <Typography variant="body2" sx={{ mt: 1 }}>{label('Toplam', 'Total')}: {formatCurrency(plan.totalAmount)} · {label('Ödenen', 'Paid')}: {formatCurrency(plan.paidAmount)} · {label('Kalan', 'Remaining')}: {formatCurrency(plan.remainingAmount)}</Typography>
          <Box component="details" sx={{ mt: 1 }}>
            <Typography component="summary" sx={{ cursor: 'pointer' }}>{label('Taksitleri göster', 'Show installments')}</Typography>
            {plan.installments.map(item => <Stack key={item.id} direction="row" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ py: 0.8, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2">{item.sequenceNo}. {item.dueDate} · {formatCurrency(item.amount)}</Typography>
              <Typography variant="body2" color={item.overdue ? 'error.main' : 'text.secondary'}>{statusLabel(item.status)} · {label('Kalan', 'Remaining')}: {formatCurrency(item.remainingAmount)}</Typography>
            </Stack>)}
          </Box>
          {plan.payments.length > 0 && <Box component="details" sx={{ mt: 1 }}>
            <Typography component="summary" sx={{ cursor: 'pointer' }}>{label('Ödeme geçmişi', 'Payment history')}</Typography>
            {plan.payments.map(payment => <Stack key={payment.debtId} direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="body2">{payment.transactionDateTime.slice(0, 10)} · {formatCurrency(payment.amount)} {payment.reversed && `· ${label('Geri alındı', 'Reversed')}`}</Typography>
              {canManage && !payment.reversed && <Button size="small" color="error" onClick={() => { setError(''); setReversal({ planId: plan.id, paymentId: payment.debtId }); }}>{label('Geri al', 'Reverse')}</Button>}
            </Stack>)}
          </Box>}
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <Button size="small" disabled={busy} onClick={() => void downloadPlan(plan)}>{label('PDF indir', 'Download PDF')}</Button>
            <Button size="small" component="a" href={buildWhatsAppUrl({ phoneNumber: customerPhone, message: planMessage(plan) })} target="_blank" rel="noopener noreferrer">{label('WhatsApp ile paylaş', 'Share on WhatsApp')}</Button>
            {plan.status === 'ACTIVE' && <Button size="small" component="a" href={buildWhatsAppUrl({ phoneNumber: customerPhone, message: reminderMessage(plan) })} target="_blank" rel="noopener noreferrer">{label('Vade hatırlatması', 'Due date reminder')}</Button>}
          </Stack>
          {canManage && plan.status === 'ACTIVE' && <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" onClick={() => { setError(''); setPaymentAmount(String(plan.remainingAmount)); setPaymentPlan(plan); }}>{label('Ödeme al', 'Record payment')}</Button>
            {plan.payments.length === 0 && <Button size="small" onClick={() => {
              setError(''); setEditing(plan); setTitle(plan.title || ''); setAmount(String(plan.totalAmount));
              setCount(String(plan.installments.length)); setFirstDueDate(plan.installments[0]?.dueDate || today()); setCreateOpen(true);
            }}>{label('Düzenle', 'Edit')}</Button>}
            <Button size="small" color="error" onClick={() => { setError(''); setCancelPlan(plan); }}>{label('Planı iptal et', 'Cancel plan')}</Button>
          </Stack>}
        </Paper>)}
      </Stack>
    </Paper>

    <AppDialog presentation="workflow" open={createOpen} onClose={() => !busy && setCreateOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? label('Ödeme planını düzenle', 'Edit payment plan') : label('Taksitli borç oluştur', 'Create installment debt')}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label={label('Açıklama', 'Description')} value={title} onChange={e => setTitle(e.target.value)} inputProps={{ maxLength: 255 }} />
        <TextField label={label('Toplam tutar', 'Total amount')} type="number" value={amount} onChange={e => setAmount(e.target.value)} disabled={Boolean(editing)} inputProps={{ min: 0.01, step: 0.01 }} />
        <TextField label={label('Taksit sayısı', 'Installment count')} type="number" value={count} onChange={e => setCount(e.target.value)} inputProps={{ min: 1, max: 120 }} />
        <TextField label={label('İlk vade', 'First due date')} type="date" value={firstDueDate} onChange={e => setFirstDueDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        {preview.length > 0 && <Box sx={{ maxHeight: 180, overflow: 'auto' }} aria-label={label('Taksit önizlemesi', 'Installment preview')}>
          {preview.map((p, i) => <Typography variant="body2" key={i}>{i + 1}. {p.date} — {formatCurrency(p.cents / 100)}</Typography>)}
        </Box>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setCreateOpen(false)}>{label('Vazgeç', 'Cancel')}</Button>
        <Button disabled={busy || preview.length === 0} variant="contained" onClick={() => void run(() => editing
          ? apiClient.paymentPlans.update(editing.id, { title: title.trim(), installmentCount: Number(count), firstDueDate })
          : apiClient.paymentPlans.create({ customerId, description: title.trim() || undefined, totalAmount: Number(amount), installmentCount: Number(count), firstDueDate }))}>{label('Kaydet', 'Save')}</Button></DialogActions>
    </AppDialog>

    <AppDialog presentation="workflow" open={Boolean(paymentPlan)} onClose={() => !busy && setPaymentPlan(null)} fullWidth maxWidth="xs">
      <DialogTitle>{label('Taksit ödemesi al', 'Record installment payment')}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body2">{label('Ödeme en eski açık taksitten başlayarak dağıtılır.', 'Payment is applied to the oldest unpaid installment first.')}</Typography>
        <TextField label={label('Ödeme tutarı', 'Payment amount')} type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} inputProps={{ min: 0.01, step: 0.01 }} />
        {paymentPreview.map(item => <Typography key={item.sequence} variant="body2">{item.sequence}. {label('taksit', 'installment')}: {formatCurrency(item.cents / 100)}</Typography>)}
        {paymentPlan && !paymentPlan.saleId && previewRemainingCents > 0 && <Typography variant="body2" color="text.secondary">{label('Cari hesaba kalan', 'Unallocated account payment')}: {formatCurrency(previewRemainingCents / 100)}</Typography>}
        {paymentPlan?.saleId && <TextField select label={label('Ödeme yöntemi', 'Payment method')} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value as PaymentMethod)}>
          <MenuItem value={PaymentMethod.CASH}>{label('Nakit', 'Cash')}</MenuItem>
          <MenuItem value={PaymentMethod.CARD}>{label('Kart', 'Card')}</MenuItem>
          <MenuItem value={PaymentMethod.BANK_TRANSFER}>{label('Banka havalesi', 'Bank transfer')}</MenuItem>
          <MenuItem value={PaymentMethod.OTHER}>{label('Diğer', 'Other')}</MenuItem>
        </TextField>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setPaymentPlan(null)}>{label('Vazgeç', 'Cancel')}</Button>
        <Button variant="contained" disabled={busy || !/^\d+(\.\d{1,2})?$/.test(paymentAmount) || Number(paymentAmount) <= 0}
          onClick={() => paymentPlan && void run(() => paymentPlan.saleId
            ? apiClient.addPaymentToSale(paymentPlan.saleId, { amount: Number(paymentAmount), paymentMethod })
            : apiClient.paymentPlans.pay(paymentPlan.id, Number(paymentAmount)))}>{label('Ödemeyi kaydet', 'Save payment')}</Button></DialogActions>
    </AppDialog>

    <AppDialog presentation="workflow" open={Boolean(cancelPlan)} onClose={() => !busy && setCancelPlan(null)} fullWidth maxWidth="xs">
      <DialogTitle>{label('Ödeme planını iptal et', 'Cancel payment plan')}</DialogTitle>
      <DialogContent><Typography>{label('Plan iptal edilir; cari borç ve ödeme geçmişi korunur.', 'The plan will be cancelled; the account balance and payment history will remain.')}</Typography>{error && <Alert severity="error">{error}</Alert>}</DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setCancelPlan(null)}>{label('Vazgeç', 'Back')}</Button>
        <Button color="error" disabled={busy} onClick={() => cancelPlan && void run(() => apiClient.paymentPlans.cancel(cancelPlan.id))}>{label('Planı iptal et', 'Cancel plan')}</Button></DialogActions>
    </AppDialog>

    <AppDialog presentation="workflow" open={Boolean(reversal)} onClose={() => !busy && setReversal(null)} fullWidth maxWidth="xs">
      <DialogTitle>{label('Ödemeyi geri al', 'Reverse payment')}</DialogTitle>
      <DialogContent><Typography>{label('Ödeme geri alınacak ve cari bakiye yeniden hesaplanacak.', 'The payment will be reversed and the account balance updated.')}</Typography>{error && <Alert severity="error">{error}</Alert>}</DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setReversal(null)}>{label('Vazgeç', 'Back')}</Button>
        <Button color="error" disabled={busy} onClick={() => reversal && void run(() => apiClient.paymentPlans.reverse(reversal.planId, reversal.paymentId))}>{label('Ödemeyi geri al', 'Reverse payment')}</Button></DialogActions>
    </AppDialog>
  </Box>;
}
